import React, {useEffect, useMemo, useState} from 'react'
import {useFormik} from 'formik'
import {KTSVG} from '../../../../../_metronic/helpers'
import {
  createCommentaireClientDemande,
  finishActions,
  getCommentaireClientDemandeByClientDemandeId,
  readMesActions,
} from '../../../../../components/requests'
import {ModeleCommentaireClientDemande} from '../../../../../components/Models'
import {useAuth} from '../../../../modules/auth'
import {LoadingData} from '../../../../../components/LoadingData'
import ModalActions from '../ModalActions'
import {toast} from 'react-toastify'
import Swal from 'sweetalert2'
import moment from 'moment'
import {shouldDisplayFonctionnalite} from '../../../../../components/helpers'

const uploadDir = `${process.env.REACT_APP_API_URL ?? ''}/uploads/serviceRattachement`

interface FormValues {
  idClientDemande: string | undefined
  clientId: number
  client?: any
  dataAction?: any
  setDemandeClient?: any
}

const CardCommentaire = (props) => {
  const {idClientDemande, clientId, client, setDemandeClient}: FormValues = props
  const {currentUser} = useAuth()
  const [pending, setPending] = useState<boolean>(false)
  const [commentaires, setCommentaires] = useState<any>([])
  const [showAllDetails, setShowAllDetails] = useState<boolean>(false)
  const [hideSection, setHideSection] = useState<boolean>(false)
  const [dataAction, setDataAction] = useState<any>(null)
  const [readOnly, setReadOnly] = useState<boolean>(false)
  const [isRealisation, setIsRealisation] = useState<boolean>(false)
  const [statutButton, setStatutButton] = useState<string | null>(null)

  const formik = useFormik({
    initialValues: {message: ''},
    onSubmit: async (values, {resetForm}) => {
      if (!values.message.trim()) return

      const nouveauCommentaire: ModeleCommentaireClientDemande = {
        idClientDemande,
        message: values.message.trim(),
        description: values.message.trim(),
        auteur: currentUser?.nom ?? currentUser?.pseudo,
      }

      try {
        setPending(true)
        const res = await createCommentaireClientDemande(nouveauCommentaire)
        if (res?.status === 200) setCommentaires((prev) => [res.data, ...prev])
        setPending(false)
        resetForm()
      } catch (err) {
        console.error("Erreur lors de l'envoi du commentaire", err)
      }
    },
  })

  useEffect(() => {
    const getCommentaires = async () => {
      if (!idClientDemande) return
      try {
        setPending(true)
        const res = await getCommentaireClientDemandeByClientDemandeId(idClientDemande)
        if (res?.status === 200 && res.data) {
          setCommentaires(res.data.reverse())
        }
      } catch (error) {
        console.error('Erreur chargement commentaires', error)
      } finally {
        setPending(false)
      }
    }
    getCommentaires()
  }, [])

  // Action case à cocher Voir tous les détails
  const filteredData = useMemo(() => {
    if (showAllDetails) {
      return commentaires
    }
    return commentaires.filter(
      (item) =>
        item?.entite !== 'ClientDemande' ||
        item?.commentaire?.includes("Création d'une demande") ||
        item?.commentaire?.includes("Attribution d'une nouvelle équipe") ||
        item?.commentaire?.includes('Client demande etape')
    )
  }, [commentaires, showAllDetails])

  const clearInput = () => {
    // Pour vider le textarea après la soumission
    formik.setFieldValue('message', '')
  }

  const handleRead = async (action) => {
    const idToast = toast.loading('Traitement en cours...')
    try {
      const res = await readMesActions(action)
      if (res?.status === 200 && res.data) {
        onUpdateComment(res.data)
        toast.update(idToast, {
          render: 'Action lue',
          type: 'success',
          isLoading: false,
          autoClose: 2000,
        })
      }
    } catch (error) {
      console.error("Erreur lors de la lecture de l'action", error)
      toast.update(idToast, {
        render: "Erreur lors de la lecture de l'action",
        type: 'error',
        isLoading: false,
        autoClose: 2000,
      })
    }
  }

  const confirmRealisation = (action) => {
    Swal.fire({
      title: 'Attention',
      text: 'Etes-vous sûr de vouloir valider la réalisation de cette action? Son statut passera en "Réalisé" et la demande retournera dans son flux de suivi classique',
      icon: 'warning',
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      showCancelButton: true,
      confirmButtonColor: '#1BC5BD',
      cancelButtonColor: '#F64E60',
    }).then((alertResult) => {
      if (alertResult.isConfirmed) {
        finishAction(action)
      }
    })
  }

  const finishAction = async (action) => {
    const idToast = toast.loading('Traitement en cours...')
    try {
      const res = await finishActions(action)
      if (res?.status === 200 && res.data) {
        onUpdateComment(res.data)
        toast.update(idToast, {
          render: 'Action réalisée',
          type: 'success',
          isLoading: false,
          autoClose: 2000,
        })
      }
    } catch (error) {
      console.error("Erreur lors du traitement de l'action", error)
      toast.update(idToast, {
        render: "Erreur lors du traitement de l'action",
        type: 'error',
        isLoading: false,
        autoClose: 2000,
      })
    }
  }

  const onAddComment = (newVal) => {
    // setCommentaires(prev => [newVal, ...prev].sort((a, b) => moment(b.dateEcheance).valueOf() - moment(a.dateEcheance).valueOf()))
    setCommentaires((prev) => [newVal, ...prev])
    setDemandeClient(newVal?.demande)
  }

  const onUpdateComment = (newVal) => {
    setCommentaires(commentaires.map((c) => (c.id === newVal.id && c.titre ? newVal : c)))
    setDemandeClient(newVal?.demande)
  }

  return (
    <div className={`card mb-5 mb-xl-10 border ${hideSection ? 'd-none' : ''}`}>
      <div className='card-header p-5 d-flex flex-column gap-3'>
        <div className='d-flex justify-content-between align-items-center w-100'>
          <div className='d-flex align-items-center gap-5'>
            <div className='card-title m-0 fw-bold fs-5'>Fil des modifications</div>

            <div className='form-check form-check-custom form-check-solid'>
              <input
                className='form-check-input h-20px w-20px'
                type='checkbox'
                id='voirDetails'
                checked={showAllDetails}
                onChange={(e) => setShowAllDetails(e.target.checked)}
              />
              <label
                className='form-check-label fw-semibold text-gray-700 fs-7 ms-2'
                htmlFor='voirDetails'
              >
                Voir tous les détails
              </label>
            </div>
          </div>

          <button
            type='button'
            className='btn btn-sm btn-icon btn-active-color-primary'
            onClick={() => setHideSection(!hideSection)}
            aria-label='Fermer'
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </button>
        </div>

        <form onSubmit={formik.handleSubmit} className='d-flex align-items-center w-100 gap-2'>
          <div className='flex-grow-1'>
            <textarea
              name='message'
              className='form-control form-control-flush border rounded-3 px-3 py-2 w-100'
              placeholder='Écrire un commentaire...'
              rows={1}
              style={{resize: 'none'}}
              value={formik.values.message}
              onChange={formik.handleChange}
            />
          </div>
          <div className='d-flex gap-2 shrink-0'>
            <button
              type='submit'
              className='btn btn-icon btn-success'
              disabled={!formik.values.message.trim() || pending}
            >
              {pending ? (
                <span className='spinner-border spinner-border-sm align-middle w-13px h-13px'></span>
              ) : (
                <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-2' />
              )}
            </button>
            {shouldDisplayFonctionnalite(currentUser, 'AJOUTER ACTION') && (
              <button
                type='button'
                className='btn btn-icon btn-success'
                data-tooltip-id='message-tooltip'
                data-tooltip-content='Ajouter une action'
                data-tooltip-place='left'
                title='Ajouter une action'
                disabled={!formik.values.message.trim() || pending || !props.dataAction.demande}
                data-bs-target='#modalActions'
                data-bs-toggle='modal'
                onClick={() => setDataAction(null)}
              >
                <KTSVG
                  path='/media/icons/duotune/communication/com011.svg'
                  className='svg-icon-2'
                />
              </button>
            )}
          </div>
        </form>
      </div>

      <div
        className='card-body overflow-y-auto px-5'
        style={{
          maxHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {pending && filteredData?.length === 0 ? (
          <LoadingData />
        ) : filteredData?.length === 0 ? (
          <div className='text-muted d-flex justify-content-center align-items-center h-100'>
            Aucun commentaire pour le moment.
          </div>
        ) : (
          <div className='d-flex flex-column gap-3'>
            {(() => {
              let lastDisplayedDate: string | null = null

              return filteredData.map((c, idx) => {
                const rawDate = c.dateCreation ?? c.date ?? c.createdAt
                let dateJour = ''
                let heureMinutes = ''

                if (rawDate) {
                  const d = new Date(rawDate)
                  dateJour = d.toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                  heureMinutes = d.toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})
                }

                const showSeparator = dateJour && dateJour !== lastDisplayedDate

                if (showSeparator) {
                  lastDisplayedDate = dateJour
                }

                return (
                  <div key={idx} className='w-100'>
                    {showSeparator && (
                      <div className='d-flex align-items-center my-4 text-muted fs-8'>
                        <div className='border-bottom flex-grow-1'></div>
                        <span
                          className='px-3 bg-white text-nowrap fw-bold text-uppercase'
                          style={{letterSpacing: '0.5px'}}
                        >
                          {dateJour}
                        </span>
                        <div className='border-bottom flex-grow-1'></div>
                      </div>
                    )}

                    <div className={`bg-light${c.commentaire ? '-info' : ''} rounded p-3 w-100`}>
                      <div
                        className='d-flex justify-content-between text-muted mb-1'
                        style={{fontSize: '0.75rem'}}
                      >
                        <strong>
                          {c.auteur ??
                            c.utilisateur?.nom ??
                            (c.entite === 'ClientDemande' ? client?.nom : c.assigneeA?.nom) ??
                            'Système'}
                          {c.titre && (
                            <span>
                              {' | '}Type action: {c.type?.libelle}
                            </span>
                          )}
                          {c.statut?.id && (
                            <>
                              {' '}
                              <span
                                className={`badge badge-${
                                  c.statut?.libelle === 'Lu' ? 'primary' : 'danger'
                                }`}
                                style={{fontSize: '0.75rem'}}
                              >
                                {c.statut.libelle}
                              </span>
                            </>
                          )}
                        </strong>
                        <div className='d-flex align-items-center gap-2'>
                          {c.titre && (
                            <span className='text-start'>
                              {c.dateRealisation && (
                                <span>
                                  Réalisée le:{' '}
                                  {c.dateEcheance
                                    ? moment(c.dateRealisation).format('DD-MM-YYYY HH:mm')
                                    : 'N/C'}{' '}
                                  {' | '}
                                </span>
                              )}
                              <span>
                                Date échéance:{' '}
                                {c.dateEcheance
                                  ? moment(c.dateEcheance).format('DD-MM-YYYY HH:mm')
                                  : 'N/C'}
                              </span>
                            </span>
                          )}
                          {c.entite && (
                            <span className='badge badge-info fs-9 px-2 py-1'>Auto</span>
                          )}
                          {c.titre && (
                            <span className='badge badge-primary fs-9 px-2 py-1'>Télévendeur</span>
                          )}
                          {heureMinutes && <span className='fw-semibold'>{heureMinutes}</span>}
                        </div>
                      </div>

                      <div className='d-flex justify-content-between align-items-center'>
                        <div
                          className={
                            (c?.assigneeA ?? c?.utilisateur)?.serviceRattachement
                              ? 'd-flex justify-content-between align-items-center gap-2'
                              : ''
                          }
                        >
                          {(c?.assigneeA ?? c?.utilisateur)?.serviceRattachement && (
                            <div>
                              <img
                                src={`${uploadDir}/${
                                  (c?.assigneeA ?? c?.utilisateur)?.serviceRattachement?.image
                                }`}
                                alt='Image service rattachement'
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '5px',
                                  objectFit: 'cover',
                                }}
                                loading='lazy'
                              />
                            </div>
                          )}
                          <div className={'d-flex flex-column gap-1'}>
                            <div style={{wordBreak: 'break-word', whiteSpace: 'pre-line'}}>
                              <span className={'fw-bold'}>Commentaire création :</span>{' '}
                              <span className={'text-muted'}>{c.description ?? c.commentaire}</span>
                            </div>
                            {c.commentaireResolution && (
                              <div className='d-flex justify-content-between align-items-center'>
                                <div
                                  className={
                                    (c?.assigneeA ?? c?.utilisateur)?.serviceRattachement
                                      ? 'd-flex justify-content-between align-items-center gap-2'
                                      : ''
                                  }
                                >
                                  <div
                                    style={{
                                      wordBreak: 'break-word',
                                      whiteSpace: 'pre-line',
                                    }}
                                  >
                                    <span className={'fw-bold'}>Commentaire résolution :</span>{' '}
                                    <span className={'text-info'}>{c.commentaireResolution}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className='text-end mt-1 gap-2'>
                          {c?.titre && shouldDisplayFonctionnalite(currentUser, 'TRAITER ACTION') && (
                            <div className='d-flex gap-2'>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-success'
                                data-tooltip-id='message-tooltip'
                                data-tooltip-content={
                                  c.lu ? "Visualiser l'action" : 'Marquer comme lu'
                                }
                                data-tooltip-place='left'
                                title={c.lu ? "Visualiser l'action" : 'Marquer comme lu'}
                                data-bs-target='#modalActions'
                                data-bs-toggle='modal'
                                onClick={() => {
                                  setReadOnly(true)
                                  setDataAction(c)
                                  if (c.statut.code !== 'TERMINEE') {
                                    setIsRealisation(true)
                                  } else {
                                    setStatutButton('hide')
                                  }

                                  if (!c.lu) {
                                    handleRead(c)
                                  }
                                }}
                              >
                                <i className='ki-duotone ki-magnifier fs-2'>
                                  <span className='path1'></span>
                                  <span className='path2'></span>
                                </i>
                              </button>{' '}
                              {c.statut.code !== 'TERMINEE' && shouldDisplayFonctionnalite(currentUser, 'TRAITER ACTION') && (
                                <>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-icon btn-primary'
                                    data-tooltip-id='message-tooltip'
                                    data-tooltip-content='Marquer comme réalisée'
                                    data-tooltip-place='left'
                                    title='Marquer comme réalisée'
                                    onClick={() => {
                                      confirmRealisation(c)
                                    }}
                                  >
                                    <img
                                      src='/media/svg/custom/File add.svg'
                                      width='25'
                                      alt='File add'
                                    />
                                  </button>{' '}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        )}
      </div>

      <ModalActions
        dataProps={dataAction ?? {...props?.dataAction, commentaire: formik.values.message}}
        readOnly={readOnly}
        onReadOnly={setReadOnly}
        onAddComment={onAddComment}
        onUpdateComment={onUpdateComment}
        clearInput={clearInput}
        isRealisation={isRealisation}
        onRealisation={setIsRealisation}
        statutButton={statutButton}
        onStatutButton={setStatutButton}
      />
    </div>
  )
}

export default CardCommentaire
